import React, { useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Chip,
  Button,
  TextField,
  Divider,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
} from "@mui/material";
import FolderSpecialOutlinedIcon from "@mui/icons-material/FolderSpecialOutlined";
import CompareArrowsOutlinedIcon from "@mui/icons-material/CompareArrowsOutlined";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import SendIcon from "@mui/icons-material/Send";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import FaceRetouchingNaturalIcon from "@mui/icons-material/FaceRetouchingNatural";
import AccessibilityNewOutlinedIcon from "@mui/icons-material/AccessibilityNewOutlined";
import RecordVoiceOverOutlinedIcon from "@mui/icons-material/RecordVoiceOverOutlined";
import GraphicEqOutlinedIcon from "@mui/icons-material/GraphicEqOutlined";
import ChecklistOutlinedIcon from "@mui/icons-material/ChecklistOutlined";
import PsychologyOutlinedIcon from "@mui/icons-material/PsychologyOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { toast } from "react-toastify";

import palette from "../../../common/styles/palette";
import { AiCard, AiBlocoTitulo, AiPill } from "./aiSurface";
import { AiAnalysis, AiItemFeedback } from "../../../services/sheai/SheAiChecklistTypes";
import SheAiChecklistService from "../../../services/sheai/SheAiChecklistService";
import { ChecklistIntegro } from "./ChecklistIntegroSection";
import { extrairDadosSoftSkills } from "./checklistMultimodalData";
import AuditoriaParecer from "./AuditoriaParecer";

interface ChecklistDossieSectionProps {
  debriefingId: string | number;
  analise?: AiAnalysis | null;
  revisao?: Record<string, AiItemFeedback>;
  checklist?: ChecklistIntegro | null;
  sttResult?: any;
  onReanalyzeSuccess?: () => void;
}

/** Rótulo do grupo de um momento da simulação (vem de `emocao` no pipeline). */
const GRUPO_PADRAO = "Outros Momentos";

/** As 4 facetas auditáveis por fala do avaliado. */
const FACETAS_AUDITAVEIS: Array<{
  chave: "facial" | "corporal" | "soft_audio" | "hard_skill";
  titulo: string;
  cor: string;
  fundo: string;
}> = [
  { chave: "facial", titulo: "👤 Facial (Vídeo)", cor: "#6D28D9", fundo: "rgba(139, 92, 246, 0.06)" },
  { chave: "corporal", titulo: "🧍 Corporal (Vídeo)", cor: "#7C3AED", fundo: "rgba(167, 139, 250, 0.08)" },
  { chave: "soft_audio", titulo: "🗣️ Soft Skill (Áudio)", cor: "#1E40AF", fundo: "rgba(59, 130, 246, 0.06)" },
  { chave: "hard_skill", titulo: "🩺 Hard Skill (Checklist)", cor: "#065F46", fundo: "rgba(16, 185, 129, 0.06)" },
];

const formatarMMSS = (s: number): string =>
  `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

const resultadoLabel = (res?: string | null): string => {
  if (!res) return "—";
  if (res === "done" || res === "Sim" || res === "Fez") return "Fez";
  if (res === "not_done" || res === "Não" || res === "Não fez") return "Não fez";
  if (res === "partially_done" || res === "Fez parcialmente") return "Fez parcialmente";
  return res;
};

export const ChecklistDossieSection: React.FC<ChecklistDossieSectionProps> = ({
  debriefingId,
  analise,
  revisao = {},
  checklist,
  sttResult,
  onReanalyzeSuccess,
}) => {
  // 0 = Hard Skills (Checklist), 1 = Soft Skills & Facetas, 2 = Auditoria fala a fala
  const [abaDossie, setAbaDossie] = useState<number>(0);
  const [grupoAberto, setGrupoAberto] = useState<string | null>(null);
  const [revisaoLocal, setRevisaoLocal] = useState<Record<string, AiItemFeedback>>({});
  const [salvandoItem, setSalvandoItem] = useState<string | number | null>(null);
  const [notasLocais, setNotasLocais] = useState<Record<string, string>>({});
  const [enviandoDossie, setEnviandoDossie] = useState(false);

  // Extrai itens da análise e combina com o checklist
  const itensAnalise = analise?.items || [];

  // Mapa de enunciados do checklist por item_id
  const enunciadosMap: Record<string, string> = {};
  if (checklist?.skills) {
    checklist.skills.forEach((sk) => {
      sk.items?.forEach((it) => {
        enunciadosMap[String(it.item_id)] = it.question || `Item ${it.item_id}`;
      });
    });
  }

  // Identifica divergências entre a IA e as anotações do professor (Hard Skills)
  const itensTabela = itensAnalise.map((item) => {
    const itemIdStr = String(item.item_id);
    const rev = revisao[itemIdStr];
    const enunciado = enunciadosMap[itemIdStr] || item.question || `Item ${item.item_id}`;
    const vereditoIa = resultadoLabel(item.result);
    const vereditoProfessor = rev?.action === "disagree"
      ? resultadoLabel(rev.adjusted_result)
      : rev?.action === "agree"
      ? vereditoIa
      : "Pendente / Em avaliação";

    const discordou = rev?.action === "disagree" || (rev?.action && rev.action !== "agree");
    const notaAtual = notasLocais[itemIdStr] !== undefined ? notasLocais[itemIdStr] : rev?.note || "";

    return {
      itemId: item.item_id,
      itemIdStr,
      enunciado,
      vereditoIa,
      vereditoProfessor,
      discordou,
      action: rev?.action || "agree",
      nota: notaAtual,
      critical: item.critical,
    };
  });

  // Soft Skills: dimensões auditáveis derivadas SOMENTE do que a rodada
  // trouxe (`soft_skills.assessments` + `speech_time` do grafo ADK). Antes
  // este bloco era um mock fixo de exame meníngeo — qualquer estação exibia
  // uma avaliação neurológica fabricada. Sem dado, a dimensão NÃO aparece.
  const assessments = analise?.soft_skills?.assessments;
  const speechTime = analise?.soft_skills?.speech_time;
  const wordcloud = analise?.soft_skills?.wordcloud || [];

  const leituraDeAspecto = (aspecto?: {
    summary?: string;
    score_label?: string;
  }): string =>
    [aspecto?.score_label, aspecto?.summary].filter(Boolean).join(" — ");

  const softSkillsAuditItems: Array<{
    id: string;
    dimensao: string;
    icone: React.ReactNode;
    leituraIa: string;
    observacaoIa: string;
  }> = [];

  if (assessments?.emotions?.summary) {
    softSkillsAuditItems.push({
      id: "soft_facial",
      dimensao: assessments.emotions.title || "Expressividade Facial e Emoções",
      icone: <FaceRetouchingNaturalIcon sx={{ color: palette.brand }} />,
      leituraIa: leituraDeAspecto(assessments.emotions),
      observacaoIa: (assessments.emotions.highlights || []).join(" · "),
    });
  }
  if (assessments?.posture?.summary) {
    softSkillsAuditItems.push({
      id: "soft_body",
      dimensao: assessments.posture.title || "Linguagem Corporal e Postura",
      icone: <AccessibilityNewOutlinedIcon sx={{ color: palette.lilacMid }} />,
      leituraIa: leituraDeAspecto(assessments.posture),
      observacaoIa: (assessments.posture.patterns || []).join(" · "),
    });
  }
  if (assessments?.communication?.summary) {
    softSkillsAuditItems.push({
      id: "soft_verbal",
      dimensao: assessments.communication.title || "Comunicação Verbal e Escuta Ativa",
      icone: <RecordVoiceOverOutlinedIcon sx={{ color: "#2563EB" }} />,
      leituraIa: leituraDeAspecto(assessments.communication),
      observacaoIa: (assessments.communication.indicators || []).join(" · "),
    });
  }
  if (speechTime && (speechTime.student_s > 0 || speechTime.patient_s > 0)) {
    const termos = wordcloud
      .slice(0, 6)
      .map((termo) => termo.term)
      .join(", ");
    softSkillsAuditItems.push({
      id: "stt_phonetic",
      dimensao: "Diarização e Tempo de Fala",
      icone: <GraphicEqOutlinedIcon sx={{ color: palette.emeraldDeep }} />,
      leituraIa: `Estudante ${Math.round(speechTime.student_pct)}% · Paciente ${Math.round(speechTime.patient_pct)}% do tempo de fala.`,
      observacaoIa: termos ? `Termos mais frequentes: ${termos}.` : "",
    });
  }

  // ── Auditoria fala a fala do AVALIADO, agrupada por momento clínico ──
  // O paciente/atriz é deliberadamente excluído: a nota é do aluno, e abrir
  // parecer sobre a atriz confunde o professor e polui o guidance da IA.
  const dadosMultimodais = extrairDadosSoftSkills(analise, sttResult || null);

  const falasDoAvaliado = dadosMultimodais.timelineFala.filter((t) => t.speaker === "estudante");

  const gruposDeFala: Array<{ grupo: string; falas: typeof falasDoAvaliado }> = [];
  falasDoAvaliado.forEach((fala) => {
    const grupo = fala.emocao || GRUPO_PADRAO;
    const existente = gruposDeFala.find((g) => g.grupo === grupo);
    if (existente) existente.falas.push(fala);
    else gruposDeFala.push({ grupo, falas: [fala] });
  });

  // Feedback vindo do backend + o que acabou de ser salvo nesta sessão.
  const revisaoEfetiva: Record<string, AiItemFeedback> = { ...revisao, ...revisaoLocal };

  const criterioDeFaceta = (falaId: number, faceta: string) => `turn_${falaId}::${faceta}`;

  const totalPontosFalaAFala = falasDoAvaliado.length * FACETAS_AUDITAVEIS.length;
  const totalPontosPreenchidos = falasDoAvaliado.reduce((acc, fala) => {
    return (
      acc +
      FACETAS_AUDITAVEIS.filter((f) => {
        const rev = revisaoEfetiva[criterioDeFaceta(fala.id, f.chave)];
        return !!(rev?.note?.trim() || rev?.action);
      }).length
    );
  }, 0);

  const registrarParecerLocal = (criterioId: string, fb: AiItemFeedback) => {
    setRevisaoLocal((prev) => ({ ...prev, [criterioId]: fb }));
  };

  const totalDivergenciasHard = itensTabela.filter((i) => i.discordou).length;
  const totalDivergenciasSoft = softSkillsAuditItems.filter((s) => revisao[s.id]?.action === "disagree").length;
  const totalDivergencias = totalDivergenciasHard + totalDivergenciasSoft;
  const totalAuditados = Object.keys(revisao).length;

  const handleSalvarRacional = async (itemId: string | number, action: "agree" | "disagree", adjustedResult?: string) => {
    const itemIdStr = String(itemId);
    const nota = notasLocais[itemIdStr] !== undefined ? notasLocais[itemIdStr] : revisao[itemIdStr]?.note || "";
    setSalvandoItem(itemId);

    try {
      await SheAiChecklistService.postChecklistFeedback({
        debriefing_id: debriefingId,
        item_id: itemId,
        action,
        adjusted_result: adjustedResult,
        note: nota,
      });
      toast.success(`Parecer salvo para ${itemId}!`);
    } catch (e) {
      toast.error(`Erro ao salvar parecer de ${itemId}`);
    } finally {
      setSalvandoItem(null);
    }
  };

  const handleEnviarDossieEReanalisar = async () => {
    setEnviandoDossie(true);
    try {
      await SheAiChecklistService.postReanalyze(debriefingId);
      toast.success("Dossiê Multimodal submetido com sucesso! Reanálise iniciada no pipeline da SheAI.");
      if (onReanalyzeSuccess) onReanalyzeSuccess();
    } catch (e: any) {
      toast.error(e?.response?.data?.msg || "Erro ao submeter Dossiê para reanálise.");
    } finally {
      setEnviandoDossie(false);
    }
  };

  return (
    <Box sx={{ display: "grid", gap: 3, mt: 1 }}>
      {/* Banner Principal do Dossiê Multimodal */}
      <AiCard sx={{ borderLeft: `4px solid ${palette.brand}` }}>
        <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
          <FolderSpecialOutlinedIcon sx={{ color: palette.brand, fontSize: 36, mt: 0.25 }} />
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", mb: 0.5 }}>
              <Typography sx={{ fontWeight: 700, fontSize: "1.15rem", color: palette.brandDark }}>
                Dossiê de Avaliação & Calibração Multimodal 360° da SheAI
              </Typography>
              <AiPill label="Flywheel de Aprendizado" tom="marca" />
              <AiPill label={`${totalDivergencias} Divergência(s) Mapeada(s)`} tom={totalDivergencias > 0 ? "atencao" : "sucesso"} />
            </Box>
            <Typography variant="body2" sx={{ color: palette.textMuted }}>
              O Dossiê consolida a auditoria do professor sobre o <strong>Checklist Clínico (Hard Skills)</strong> e sobre as <strong>Soft Skills & Facetas Multimodais (Face, Corpo, Áudio e Transcrição)</strong>.
              Ao entregar o Dossiê, todas as críticas e justificativas são injetadas na próxima análise para calibrar a reavaliação da simulação.
            </Typography>
          </Box>
        </Box>
      </AiCard>

      {/* Cards de Métricas e Identidade da Rodada */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: "10px", backgroundColor: palette.brandWash, border: `1px solid ${palette.lilacMid}44` }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <CompareArrowsOutlinedIcon sx={{ color: palette.brand }} />
              <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", color: palette.brandDark }}>
                Divergências Mapeadas
              </Typography>
            </Box>
            <Typography sx={{ fontSize: "1.6rem", fontWeight: 800, color: totalDivergencias > 0 ? palette.danger : palette.emeraldDeep }}>
              {totalDivergencias} <Typography component="span" variant="body2" sx={{ color: palette.textMuted }}>pontos com ajuste</Typography>
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: "10px", backgroundColor: palette.brandWash, border: `1px solid ${palette.lilacMid}44` }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <RateReviewOutlinedIcon sx={{ color: palette.brand }} />
              <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", color: palette.brandDark }}>
                Auditoria do Professor
              </Typography>
            </Box>
            <Typography sx={{ fontSize: "1.6rem", fontWeight: 800, color: palette.brandDark }}>
              {totalAuditados} <Typography component="span" variant="body2" sx={{ color: palette.textMuted }}>critérios auditados</Typography>
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: "10px", backgroundColor: palette.brandWash, border: `1px solid ${palette.lilacMid}44` }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <HistoryOutlinedIcon sx={{ color: palette.brand }} />
              <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", color: palette.brandDark }}>
                Identidade da Rodada
              </Typography>
            </Box>
            <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: palette.text }}>
              Modelo: {analise?.call?.model || analise?.model || "—"}
            </Typography>
            <Typography variant="caption" sx={{ color: palette.textMuted, display: "block" }}>
              Tokens: {analise?.call?.usage?.total_tokens != null
                ? analise.call.usage.total_tokens.toLocaleString("pt-BR")
                : "—"} · Duração: {analise?.call?.duration_s != null ? `${analise.call.duration_s}s` : "—"}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* ── NAVEGAÇÃO ENTRE AS ABAS DO DOSSIÊ: HARD SKILLS vs SOFT SKILLS & FACETAS ── */}
      <AiCard>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, flexWrap: "wrap", gap: 1 }}>
          <Tabs
            value={abaDossie}
            onChange={(_, v) => setAbaDossie(v)}
            sx={{
              "& .MuiTab-root": { textTransform: "none", fontWeight: 700, fontSize: "0.95rem" },
            }}
          >
            <Tab
              icon={<ChecklistOutlinedIcon sx={{ fontSize: 20 }} />}
              iconPosition="start"
              label={`Checklist Clínico / Hard Skills (${itensTabela.length})`}
            />
            <Tab
              icon={<PsychologyOutlinedIcon sx={{ fontSize: 20 }} />}
              iconPosition="start"
              label={`Soft Skills & Facetas Multimodais (${softSkillsAuditItems.length})`}
            />
            <Tab
              icon={<RateReviewOutlinedIcon sx={{ fontSize: 20 }} />}
              iconPosition="start"
              label={`Parecer Fala a Fala do Avaliado (${totalPontosPreenchidos}/${totalPontosFalaAFala})`}
            />
          </Tabs>

          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.5 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={enviandoDossie ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
              disabled={enviandoDossie}
              onClick={handleEnviarDossieEReanalisar}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                backgroundColor: palette.brand,
                "&:hover": { backgroundColor: palette.brandDark },
              }}
            >
              {enviandoDossie
                ? "Submetendo Dossiê…"
                : totalPontosPreenchidos < totalPontosFalaAFala
                ? "Entregar Dossiê Parcial & Reanalisar com IA"
                : "Entregar Dossiê Completo & Reanalisar com IA"}
            </Button>
            <Typography variant="caption" sx={{ color: palette.textMuted }}>
              Pareceres em branco são permitidos — só o que você escreveu vai para a calibração.
            </Typography>
          </Box>
        </Box>

        {/* ── ABA 1 DO DOSSIÊ: CHECKLIST CLÍNICO / HARD SKILLS ── */}
        {abaDossie === 0 && (
          <Box>
            <Typography variant="body2" sx={{ color: palette.textMuted, mb: 2 }}>
              Revise as classificações sugeridas pela IA para os itens técnicos do checklist. Para cada item com divergência, selecione <strong>Discorda</strong> e registre o racional clínico para calibração.
            </Typography>

            {itensTabela.length === 0 ? (
              <Alert severity="info">Nenhum item de checklist disponível para auditoria.</Alert>
            ) : (
              <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${palette.border}`, borderRadius: "8px" }}>
                <Table size="small">
                  <TableHead sx={{ backgroundColor: palette.brandWash }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, color: palette.brandDark, width: "35%" }}>Item / Enunciado</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: palette.brandDark, width: "15%" }}>Veredito da IA</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: palette.brandDark, width: "20%" }}>Decisão do Professor</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: palette.brandDark, width: "30%" }}>Racional Clínico / Justificativa</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {itensTabela.map((item) => (
                      <TableRow key={item.itemIdStr} sx={{ "&:hover": { backgroundColor: "#F8FAFC" } }}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: palette.text }}>
                            {item.enunciado}
                          </Typography>
                          <Box sx={{ display: "flex", gap: 0.5, mt: 0.5 }}>
                            <Chip label={`Item #${item.itemId}`} size="small" sx={{ fontSize: "11px", height: "20px" }} />
                            {item.critical && (
                              <Chip label="Crítico / Eliminatório" size="small" color="error" sx={{ fontSize: "11px", height: "20px", fontWeight: 700 }} />
                            )}
                          </Box>
                        </TableCell>

                        <TableCell>
                          <Chip
                            label={item.vereditoIa}
                            size="small"
                            icon={item.vereditoIa === "Fez" ? <CheckCircleOutlineIcon /> : <HighlightOffIcon />}
                            sx={{
                              fontWeight: 700,
                              backgroundColor: item.vereditoIa === "Fez" ? `${palette.emeraldDeep}15` : `${palette.danger}15`,
                              color: item.vereditoIa === "Fez" ? palette.emeraldDeep : palette.danger,
                            }}
                          />
                        </TableCell>

                        <TableCell>
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                            <Box sx={{ display: "flex", gap: 0.5 }}>
                              <Button
                                size="small"
                                variant={item.action === "agree" ? "contained" : "outlined"}
                                onClick={() => handleSalvarRacional(item.itemId, "agree")}
                                sx={{ fontSize: "11px", py: 0.25, px: 1, minWidth: "auto", textTransform: "none" }}
                              >
                                Concorda
                              </Button>
                              <Button
                                size="small"
                                color="error"
                                variant={item.action === "disagree" ? "contained" : "outlined"}
                                onClick={() => handleSalvarRacional(item.itemId, "disagree", item.vereditoIa === "Fez" ? "not_done" : "done")}
                                sx={{ fontSize: "11px", py: 0.25, px: 1, minWidth: "auto", textTransform: "none" }}
                              >
                                Discorda
                              </Button>
                            </Box>
                            {item.discordou && (
                              <Typography variant="caption" sx={{ color: palette.danger, fontWeight: 600 }}>
                                Ajustado para: {item.vereditoProfessor}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>

                        <TableCell>
                          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                            <TextField
                              size="small"
                              fullWidth
                              placeholder="Ex: Executou manobra aos 01:12..."
                              value={item.nota}
                              onChange={(e) =>
                                setNotasLocais({ ...notasLocais, [item.itemIdStr]: e.target.value })
                              }
                              onBlur={() => handleSalvarRacional(item.itemId, item.action as any, item.vereditoProfessor)}
                              sx={{ "& input": { fontSize: "12px", py: 0.75 } }}
                            />
                            {salvandoItem === item.itemId && <CircularProgress size={14} />}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {/* ── ABA 2 DO DOSSIÊ: SOFT SKILLS & FACETAS MULTIMODAIS (FACE, CORPO, ÁUDIO, STT) ── */}
        {abaDossie === 1 && (
          <Box>
            <Typography variant="body2" sx={{ color: palette.textMuted, mb: 2 }}>
              Audite e critique as interpretações da IA sobre as <strong>dimensões comportamentais e expressivas</strong> da simulação. Suas observações serão injetadas para afinar a análise de visão computacional e áudio na reanálise.
            </Typography>

            {softSkillsAuditItems.length === 0 && (
              <Alert severity="info">
                Esta rodada não trouxe leituras de soft skills para auditar.
              </Alert>
            )}

            <Grid container spacing={2}>
              {softSkillsAuditItems.map((soft) => {
                const rev = revisao[soft.id];
                const discordou = rev?.action === "disagree";
                const notaAtual = notasLocais[soft.id] !== undefined ? notasLocais[soft.id] : rev?.note || "";

                return (
                  <Grid item xs={12} key={soft.id}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        borderRadius: "10px",
                        border: discordou ? `2px solid ${palette.danger}` : `1px solid ${palette.border}`,
                        backgroundColor: discordou ? "rgba(254, 242, 242, 0.4)" : "#FFFFFF",
                      }}
                    >
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1.5, flexWrap: "wrap", gap: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          {soft.icone}
                          <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: palette.brandDark }}>
                            {soft.dimensao}
                          </Typography>
                        </Box>

                        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                          <Button
                            size="small"
                            variant={rev?.action !== "disagree" ? "contained" : "outlined"}
                            onClick={() => handleSalvarRacional(soft.id, "agree")}
                            sx={{ fontSize: "11px", py: 0.4, px: 1.5, textTransform: "none", fontWeight: 600 }}
                          >
                            ✓ Concorda com a Leitura
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            variant={discordou ? "contained" : "outlined"}
                            onClick={() => handleSalvarRacional(soft.id, "disagree", "Ajuste do Professor")}
                            sx={{ fontSize: "11px", py: 0.4, px: 1.5, textTransform: "none", fontWeight: 600 }}
                          >
                            ✕ Discorda / Ajustar
                          </Button>
                        </Box>
                      </Box>

                      {/* Leitura e Evidência da IA */}
                      <Box sx={{ p: 1.5, mb: 2, borderRadius: "6px", backgroundColor: palette.brandWash, border: `1px solid ${palette.lilacMid}33` }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: palette.brandDark, mb: 0.5 }}>
                          🤖 Diagnóstico da IA: {soft.leituraIa}
                        </Typography>
                        <Typography variant="caption" sx={{ color: palette.textMuted }}>
                          {soft.observacaoIa}
                        </Typography>
                      </Box>

                      {/* Campo de Crítica e Calibração do Professor */}
                      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                        <TextField
                          size="small"
                          fullWidth
                          label="Crítica do Professor / Instrução de Calibração para a IA"
                          placeholder="Ex: O aluno demonstrou foco adequado durante o exame, sem sinais de estresse..."
                          value={notaAtual}
                          onChange={(e) =>
                            setNotasLocais({ ...notasLocais, [soft.id]: e.target.value })
                          }
                          onBlur={() => handleSalvarRacional(soft.id, discordou ? "disagree" : "agree", "Calibrado")}
                          sx={{ "& input": { fontSize: "12px" } }}
                        />
                        {salvandoItem === soft.id && <CircularProgress size={16} />}
                      </Box>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        )}

        {/* ── ABA 3 DO DOSSIÊ: PARECER FALA A FALA DO AVALIADO, AGRUPADO POR MOMENTO ── */}
        {abaDossie === 2 && (
          <Box>
            <Typography variant="body2" sx={{ color: palette.textMuted, mb: 2 }}>
              Cada fala <strong>do avaliado</strong> é listada na ordem cronológica da simulação. Abra um grupo e registre o
              parecer por faceta. <strong>Todos os campos são opcionais</strong> — o dossiê pode ser entregue parcialmente
              preenchido.
            </Typography>

            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
              <AiPill label={`${falasDoAvaliado.length} falas do avaliado`} tom="marca" />
              <AiPill
                label={`${totalPontosPreenchidos} de ${totalPontosFalaAFala} pareceres registrados`}
                tom={totalPontosPreenchidos > 0 ? "sucesso" : "atencao"}
              />
              <AiPill label="Falas da atriz/paciente não são avaliadas" tom="neutro" />
            </Box>

            {gruposDeFala.length === 0 ? (
              <Alert severity="info">Nenhuma fala do avaliado disponível para auditoria detalhada.</Alert>
            ) : (
              gruposDeFala.map((grupo) => {
                const aberto = grupoAberto === grupo.grupo;
                const preenchidosNoGrupo = grupo.falas.reduce(
                  (acc, fala) =>
                    acc +
                    FACETAS_AUDITAVEIS.filter((f) => {
                      const rev = revisaoEfetiva[criterioDeFaceta(fala.id, f.chave)];
                      return !!(rev?.note?.trim() || rev?.action);
                    }).length,
                  0
                );
                const totalNoGrupo = grupo.falas.length * FACETAS_AUDITAVEIS.length;

                return (
                  <Paper
                    key={grupo.grupo}
                    elevation={0}
                    sx={{
                      mb: 1.5,
                      borderRadius: "10px",
                      border: `1px solid ${palette.border}`,
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      role="button"
                      aria-expanded={aberto}
                      onClick={() => setGrupoAberto(aberto ? null : grupo.grupo)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        p: 1.75,
                        cursor: "pointer",
                        backgroundColor: aberto ? palette.brandWash : "#FFFFFF",
                        "&:hover": { backgroundColor: palette.brandWash },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                        <ExpandMoreIcon
                          sx={{
                            color: palette.brand,
                            transform: aberto ? "rotate(0deg)" : "rotate(-90deg)",
                            transition: "transform 150ms",
                          }}
                        />
                        <Typography sx={{ fontWeight: 700, color: palette.brandDark }}>{grupo.grupo}</Typography>
                        <Chip
                          label={`${grupo.falas.length} fala(s)`}
                          size="small"
                          sx={{ height: 20, fontSize: "11px" }}
                        />
                      </Box>
                      <Chip
                        label={`${preenchidosNoGrupo}/${totalNoGrupo} pareceres`}
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: "11px",
                          fontWeight: 700,
                          backgroundColor:
                            preenchidosNoGrupo === 0
                              ? "#F1F5F9"
                              : preenchidosNoGrupo === totalNoGrupo
                              ? `${palette.emeraldDeep}18`
                              : `${palette.brand}15`,
                          color:
                            preenchidosNoGrupo === 0
                              ? palette.textMuted
                              : preenchidosNoGrupo === totalNoGrupo
                              ? palette.emeraldDeep
                              : palette.brandDark,
                        }}
                      />
                    </Box>

                    {aberto && (
                      <Box sx={{ p: 2, backgroundColor: "#FAFAFC", borderTop: `1px solid ${palette.border}` }}>
                        {grupo.falas.map((fala) => (
                          <Paper
                            key={fala.id}
                            elevation={0}
                            sx={{
                              p: 2,
                              mb: 2,
                              borderRadius: "8px",
                              backgroundColor: "#FFFFFF",
                              border: `1px solid ${palette.border}`,
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                              <Chip
                                label={`[▶ ${formatarMMSS(fala.start)} - ${formatarMMSS(fala.end)}]`}
                                size="small"
                                sx={{ fontWeight: 700, backgroundColor: palette.brandWash, color: palette.brand }}
                              />
                              <Chip label="🧑‍⚕️ Avaliado" size="small" sx={{ fontWeight: 700 }} />
                            </Box>

                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 700, color: palette.brandDark, mb: 1.5, fontStyle: "italic" }}
                            >
                              {`"${fala.texto}"`}
                            </Typography>

                            <Grid container spacing={1.5}>
                              {FACETAS_AUDITAVEIS.map((faceta) => {
                                const leituraIa =
                                  faceta.chave === "facial"
                                    ? fala.facetas.facial.expressao
                                    : faceta.chave === "corporal"
                                    ? fala.facetas.corporal.postura
                                    : faceta.chave === "soft_audio"
                                    ? fala.facetas.softSkillAudio.tomVoz
                                    : fala.facetas.hardSkillVideo.tecnicaMedica;
                                const criterioId = criterioDeFaceta(fala.id, faceta.chave);

                                return (
                                  <Grid item xs={12} sm={6} md={3} key={faceta.chave}>
                                    <Paper
                                      elevation={0}
                                      sx={{
                                        p: 1.25,
                                        height: "100%",
                                        backgroundColor: faceta.fundo,
                                        border: `1px solid ${faceta.cor}33`,
                                      }}
                                    >
                                      <Typography
                                        sx={{ fontWeight: 700, fontSize: "11px", color: faceta.cor, mb: 0.5 }}
                                      >
                                        {faceta.titulo}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        sx={{ display: "block", color: palette.text, mb: 0.5 }}
                                      >
                                        {leituraIa}
                                      </Typography>
                                      <AuditoriaParecer
                                        criterioId={criterioId}
                                        debriefingId={debriefingId}
                                        feedback={revisaoEfetiva[criterioId]}
                                        compacto
                                        comVeredito
                                        placeholder="Parecer do professor (opcional)"
                                        onSalvo={registrarParecerLocal}
                                      />
                                    </Paper>
                                  </Grid>
                                );
                              })}
                            </Grid>
                          </Paper>
                        ))}
                      </Box>
                    )}
                  </Paper>
                );
              })
            )}
          </Box>
        )}
      </AiCard>
    </Box>
  );
};

export default ChecklistDossieSection;
